<template>
  <AgGridVue
    v-bind="gridOptions"
    class="ag-theme-alpine salve-theme-packing"
  />
  <SwapModal />
</template>

<script setup>
import { watch, inject } from 'vue'
import { AgGridVue } from 'ag-grid-vue3'
import QuantityCell from '@/Pages/Fulfillment/DataGrid/QuantityCell'
import ProductCell from '@/Pages/Fulfillment/DataGrid/ProductCell'
import SwapCell from '@/Pages/Fulfillment/DataGrid/SwapCell'
import SwapHeader from '@/Pages/Fulfillment/DataGrid/SwapHeader.vue'
import QuantityHeader from '@/Pages/Fulfillment/DataGrid/QuantityHeader.vue'
import DollarCell from '@/Components/DataGrid/Cells/DollarCell.vue'
import SwapModal from '@/Pages/Fulfillment/SwapModal.vue'
import ResetCell from '@/Pages/Fulfillment/DataGrid/ResetCell.vue'
import ResetHeader from '@/Pages/Fulfillment/DataGrid/ResetHeader.vue'
import { FULFILLMENT_SERVICE } from '@/Composables/Fulfillment/constants'
import { useGridApi } from '@/Components/DataGrid/grid-api'

const props = defineProps({
  order: {
    type: Object,
    required: true
  },
  showSwapControls: {
    type: Boolean,
    default: true
  },
  showResetControls: {
    type: Boolean,
    default: true
  }
})

const service = inject(FULFILLMENT_SERVICE)

const { gridApi, createGridOptions } = useGridApi()

watch(gridApi, () => service.syncOrder(), { once: true })

watch(service.tableData, () => {
  gridApi.value.setGridOption('rowData', service.tableData.value)
})

watch(
  () => props.showSwapControls,
  (shouldShow) => {
    gridApi.value.setColumnsVisible(['swap'], shouldShow)
  }
)

watch(
  () => props.showResetControls,
  (shouldShow) => {
    gridApi.value.setColumnsVisible(['reset'], shouldShow)
  }
)

const gridOptions = createGridOptions({
  onGridReady: (params) => {
    gridApi.value = params.api
  },
  getRowId: (params) => {
    return params.data.sub_lot_num
  },
  columnDefs: [
    {
      headerName: 'Qty',
      headerComponent: QuantityHeader,
      field: '_quantity_completed',
      cellRenderer: QuantityCell,
      width: 100,
      valueGetter: (params) => {
        if (params.data._dirty === undefined) {
          params.data._dirty = false
        } else {
          params.data._dirty = true
        }
        return `${params.data._quantity_completed}-${params.data.quantity}`
      },
      cellRendererParams: (params) => ({
        required: params.data.quantity,
        completed: params.data._quantity_completed,
        onClick: () => {
          service.updateItem({ identifier: params.data.sub_lot_num })
        }
      }),
      pinned: 'left'
    },
    {
      headerName: 'Product',
      field: 'product_name',
      cellRenderer: ProductCell,
      flex: 1,
      minWidth: 350
    },
    {
      headerName: 'Tag Number',
      field: 'tag_num',
      width: 200
    },
    {
      headerName: 'Price',
      field: 'price',
      width: 80,
      cellRenderer: DollarCell
    },
    {
      headerName: 'Subtotal',
      field: 'subtotal',
      width: 100,
      cellRenderer: DollarCell
    },
    {
      headerName: 'Discount',
      field: 'total_discount',
      width: 100,
      cellRenderer: DollarCell
    },
    {
      headerName: 'Total',
      field: 'total',
      width: 80,
      cellRenderer: DollarCell
    },
    {
      headerName: 'Swap',
      headerComponent: SwapHeader,
      cellRenderer: SwapCell,
      cellRendererParams: (params) => ({
        canSwap: params.data._quantity_completed < params.data.quantity,
        onSwap: () => {
          service.swap.trigger({
            productName: params.data.product_name,
            productId: params.data.product_id,
            subLotNum: params.data.sub_lot_num,
            quantity: params.data.quantity,
            quantityFulfilled: params.data._quantity_completed
          })
        }
      }),
      width: 60,
      hide: !props.showSwapControls
    },
    {
      headerName: 'Reset',
      headerComponent: ResetHeader,
      cellRenderer: ResetCell,
      cellRendererParams: (params) => ({
        canReset: params.data._quantity_completed > 0,
        onReset: () => {
          service.updateItem({
            identifier: params.data.sub_lot_num,
            customQtyAdjustment: params.data._quantity_completed * -1
          })
        }
      }),
      width: 60,
      hide: !props.showResetControls
    }
  ],

  suppressRowHoverHighlight: true,
  getRowStyle: (params) => {
    return {
      'background-color': params.data._quantity_completed === params.data.quantity ? '#FCFFFB' : '#FFFFFF'
    }
  },

  defaultColDef: {
    floatingFilter: false,
    suppressHeaderMenuButton: true
  },

  suppressMovableColumns: true,
  pagination: false,
  animateRows: false,
  sideBar: null,
  domLayout: 'autoHeight',
  suppressContextMenu: true
})
</script>
